import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminPages } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { formatPrice } from '@dashboard/services/formatting';
import getDisplayPrice from '@dashboard/services/getDisplayPrice';
import { formatPageCellLabel } from './pageCellLabels';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import StatusBadge from '@dashboard/components/StatusBadge';

const Pages = () => {
  const formatCreationSource = creationSource => {
    switch (creationSource) {
      case 'mcp':
        return 'MCP';
      case 'seller_api':
        return 'API';
      case 'dashboard':
        return 'Dash';
      case 'dashboard_ai':
        return 'Dash AI';
      default:
        return '';
    }
  };

  const history = useHistory();

  const query = useUrlQuery();
  const { search, searchInput, setSearchInput } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [pageType, setPageType] = useState(query.pageType || '');
  const [usageType, setUsageType] = useState(query.usageType || '');
  const [hostedLayoutType, setHostedLayoutType] = useState(query.hostedLayoutType || '');
  const [siteLayoutType, setSiteLayoutType] = useState(query.siteLayoutType || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(query.subscriptionStatus || '');
  const [hasOrderBumps, setHasOrderBumps] = useState(query.hasOrderBumps === true);

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(pageType && { pageType }),
        ...(usageType && { usageType }),
        ...(hostedLayoutType && { hostedLayoutType }),
        ...(siteLayoutType && { siteLayoutType }),
        ...(subscriptionStatus && { subscriptionStatus }),
        ...(hasOrderBumps && { hasOrderBumps: true }),
      }),
    });
  }, [
    sortBy,
    sortDirection,
    search,
    pageType,
    usageType,
    hostedLayoutType,
    siteLayoutType,
    subscriptionStatus,
    hasOrderBumps,
  ]);

  useEffect(() => {
    if (usageType === 'hosted') setSiteLayoutType('');
    if (usageType === 'site') setHostedLayoutType('');
  }, [usageType]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    pageType,
    usageType,
    hostedLayoutType,
    siteLayoutType,
    subscriptionStatus,
    hasOrderBumps,
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminPages(filters);

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Page" />,
        cell: ({ row }) => {
          const checkout = row.original;
          return (
            <div className="max-w-xs">
              <Link href={`/pages/${checkout._id}/customize`}>{checkout.name}</Link>
              {checkout.status === 'archived' && <StatusBadge status={checkout.status} />}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'product.price',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Price" />,
        cell: ({ row }) => {
          const checkout = row.original;
          if (checkout.type === 'checkout' && checkout.product) {
            return formatPrice(getDisplayPrice(checkout.product));
          }
          return null;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'type',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Type" />,
        cell: ({ row }) => formatPageCellLabel(row.getValue('type')),
        enableSorting: true,
      },
      {
        accessorKey: 'creationSource',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Src" />,
        cell: ({ row }) => formatCreationSource(row.getValue('creationSource')),
        enableSorting: false,
      },
      {
        accessorKey: 'usageType',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Usage" />,
        cell: ({ row }) => formatPageCellLabel(row.getValue('usageType')),
        enableSorting: true,
      },
      {
        accessorKey: 'layout',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Layout" />,
        cell: ({ row }) => {
          const checkout = row.original;
          if (checkout.usageType === 'hosted') {
            return formatPageCellLabel(checkout.hostedLayoutType);
          }
          if (checkout.usageType === 'site') {
            return formatPageCellLabel(checkout.siteLayoutType);
          }
          return null;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'seller.displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Merchant" />,
        cell: ({ row }) => {
          const checkout = row.original;
          if (!checkout.seller) {
            return (
              <span className="text-zinc-400 dark:text-muted-foreground">Merchant deleted</span>
            );
          }
          return (
            <div className="relative max-w-xs">
              <Link href={`/admin/merchants/${checkout.seller._id}`}>
                {checkout.seller.displayName || checkout.seller.name}
              </Link>
              {checkout.seller.disabled && (
                <Badge variant="secondary" className="ml-2 absolute right-0 mt-0.5">
                  Disabled
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'product.chargeCount',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Payments" />,
        cell: ({ row }) => {
          const checkout = row.original;
          if (checkout.type === 'checkout' && checkout.product) {
            return checkout.product.chargeCount;
          }
          if (checkout.type === 'form') {
            return checkout.submissionCount;
          }
          return null;
        },
        enableSorting: true,
      },
      {
        id: 'orderBumps',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Bumps" />,
        cell: ({ row }) => {
          const bumps = row.original.orderBumps ?? [];
          if (!bumps.length) return null;
          const disabledCount = bumps.filter(bump => bump.enabled === false).length;
          return disabledCount ? `${bumps.length} (${disabledCount} off)` : bumps.length;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date created" />,
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'dd MMM yyyy HH:mm') : '';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'updatedAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Last updated" />,
        cell: ({ row }) => {
          const updatedAt = row.getValue('updatedAt');
          return updatedAt ? format(new Date(updatedAt), 'dd MMM yyyy HH:mm') : '';
        },
        enableSorting: true,
      },
    ],
    [],
  );

  // Filter controls
  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto" />

      <div>
        <Label htmlFor="subscriptionStatus">Merchant subscription status</Label>
        <Select
          name="subscriptionStatus"
          value={subscriptionStatus || 'all'}
          onValueChange={value => setSubscriptionStatus(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trial_expired">Trial expired</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="legacy">Legacy</SelectItem>
            <SelectItem value="comped">Comped</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="pageType">Page type</Label>
        <Select
          name="pageType"
          value={pageType || 'all'}
          onValueChange={value => setPageType(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="checkout">Checkout</SelectItem>
            <SelectItem value="event">Event</SelectItem>
            <SelectItem value="form">Form</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="hasOrderBumps">Order bumps</Label>
        <Select
          name="hasOrderBumps"
          value={hasOrderBumps ? 'with' : 'all'}
          onValueChange={value => setHasOrderBumps(value === 'with')}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="with">With order bumps</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="usageType">Usage type</Label>
        <Select
          name="usageType"
          value={usageType || 'all'}
          onValueChange={value => setUsageType(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="hosted">Hosted</SelectItem>
            <SelectItem value="site">Site</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {usageType === 'hosted' && (
        <div>
          <Label htmlFor="hostedLayoutType">Hosted layout type</Label>
          <Select
            name="hostedLayoutType"
            value={hostedLayoutType || 'all'}
            onValueChange={value => setHostedLayoutType(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="double">Double</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="split">Split</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {usageType === 'site' && (
        <div>
          <Label htmlFor="siteLayoutType">Site layout type</Label>
          <Select
            name="siteLayoutType"
            value={siteLayoutType || 'all'}
            onValueChange={value => setSiteLayoutType(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="embed">Embed</SelectItem>
              <SelectItem value="popup">Pop-up</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by checkout name, product title"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Pages</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No pages found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default Pages;
